'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { ThemeToggle } from '../../components/ThemeToggle';
import { getAuthSession } from '../../lib/authSession';
import { useLandingLocale, useLandingTranslations } from './LandingLocaleProvider';
import { ContextLayerSection } from './ContextLayerSection';
import { DesktopDownloadSection } from './DesktopDownloadSection';
import { WorkspaceHostSection } from './WorkspaceHostSection';
import { Reveal } from './Reveal';
import { AnimatedPhoneMockup } from './AnimatedPhoneMockup';
import { AnimatedTerminalMockup } from './AnimatedTerminalMockup';
import { SwimlaneDiagram } from './SwimlaneDiagram';
import { WhatYouGetSection } from './WhatYouGetSection';

const agents = [
  { name: 'Claude Code', icon: '/agents/claude.svg', mono: false },
  { name: 'Codex', icon: '/agents/openai.svg', mono: true },
  { name: 'Copilot CLI', icon: '/agents/copilot.svg', mono: true },
  { name: 'OpenCode', icon: '/agents/opencode.svg', mono: true },
  { name: 'Amp', icon: '/agents/ampcode.svg', mono: true },
  { name: 'Cursor', icon: '/agents/cursor.svg', mono: true },
  { name: 'Orca', icon: '/agents/orca.svg', mono: true },
  { name: 'herdr', icon: '/agents/herdr.png', mono: true },
  { name: 'Kimi CLI', icon: '/agents/kimi.svg', mono: false },
  { name: 'Antigravity', icon: '/agents/antigravity.svg', mono: false },
  { name: 'Kiro CLI', icon: '/agents/kiro.svg', mono: false },
  { name: 'Grok Build', icon: '/agents/grok.svg', mono: true },
  { name: 'Oh My Pi', icon: '/agents/omp.svg', mono: true },
  { name: 'Muse Code', icon: '/agents/muse.svg', mono: true },
];

export function LandingContent() {
  const t = useLandingTranslations();
  const router = useRouter();
  const searchParams = useSearchParams();
  const { locale, toggleLocale } = useLandingLocale();
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const hasCheckedRef = useRef(false);

  useEffect(() => {
    if (hasCheckedRef.current) return;
    hasCheckedRef.current = true;

    const session = getAuthSession();
    if (session && !searchParams.has('preview')) {
      router.replace('/home');
      return;
    }

    setIsCheckingAuth(false);
  }, [router, searchParams]);

  if (isCheckingAuth) {
    return null;
  }

  return (
    <main className="min-h-screen bg-base-200 text-base-content">
      <div className="mx-auto flex min-h-screen w-full max-w-6xl flex-col px-4 py-6 md:px-6 md:py-10">
        {/* Header */}
        <header className="sticky top-2 z-20 mb-8 rounded-2xl border border-base-300/70 bg-base-100 px-4 py-3 md:px-6">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-9 w-9"
                viewBox="0 0 512 512"
                role="img"
                aria-label="AgentTeams"
              >
                <defs>
                  <linearGradient id="landing-logo-bg" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#3b82f6" />
                    <stop offset="100%" stopColor="#1d4ed8" />
                  </linearGradient>
                </defs>
                <rect width="512" height="512" rx="96" fill="url(#landing-logo-bg)" />
                <path
                  d="M152 384L256 128l104 256"
                  fill="none"
                  stroke="#ffffff"
                  strokeWidth="36"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <line x1="196" y1="290" x2="316" y2="290" stroke="#ffffff" strokeWidth="36" strokeLinecap="round" />
                <circle cx="256" cy="128" r="26" fill="#ffffff" />
                <circle cx="152" cy="384" r="26" fill="#ffffff" />
                <circle cx="360" cy="384" r="26" fill="#ffffff" />
              </svg>
              <div>
                <p className="hidden text-sm font-semibold tracking-wide sm:block">AgentTeams</p>
                <p className="hidden text-xs text-base-content/70 sm:block">{t('brandSubtitle')}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <ThemeToggle persistPreference={false} initialMode="LIGHT" framed={false} />
              <button type="button" onClick={toggleLocale} className="btn btn-ghost btn-sm text-xs font-medium">
                {locale === 'en' ? '한국어' : 'EN'}
              </button>
              <Link href="/auth/login" className="btn btn-ghost btn-sm hidden sm:inline-flex">
                {t('login')}
              </Link>
              <Link href="/auth/register" className="btn btn-primary btn-sm">
                {t('signUp')}
              </Link>
            </div>
          </div>
        </header>

        {/* 1. Hero */}
        <section className="relative overflow-hidden rounded-2xl border border-base-300 bg-base-100 p-6 shadow-xs md:p-10">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(59,130,246,0.12),transparent_32%),radial-gradient(circle_at_left_center,rgba(16,185,129,0.08),transparent_28%)]"
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: 'radial-gradient(circle, currentColor 1px, transparent 1px)',
              backgroundSize: '24px 24px',
            }}
          />
          <div className="relative grid gap-8 lg:grid-cols-[1.25fr_0.75fr] lg:items-center">
            <div>
              <p className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-primary">{t('heroEyebrow')}</p>
              <h1 className="text-3xl font-bold leading-tight md:text-5xl">
                {t('heroTitleLine1')}
                <br className="hidden md:block" /> {t('heroTitleLine2')}
              </h1>
              <p className="mt-4 max-w-2xl text-sm leading-relaxed text-base-content/70 md:text-base">
                {t('heroDescription')}
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                {(['heroProof1', 'heroProof2', 'heroProof3'] as const).map((key) => (
                  <span
                    key={key}
                    className="inline-flex items-center gap-1.5 rounded-full border border-base-300 bg-base-100/70 px-3 py-1.5 text-xs font-medium text-base-content/70"
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                    {t(key)}
                  </span>
                ))}
              </div>
              <div className="mt-6 flex flex-wrap items-center gap-4">
                <div className="flex flex-wrap gap-2">
                  <Link href="/auth/register" className="btn btn-primary">
                    {t('primaryCta')}
                  </Link>
                  <a href="#what-you-get" className="btn btn-outline">
                    {t('heroSecondaryCta')}
                  </a>
                </div>
                <div>
                  <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-base-content/40">
                    {t('supportedAgentsLabel')}
                  </p>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {agents.map((agent) => (
                      <span
                        key={agent.name}
                        className="inline-flex items-center rounded-full border border-base-300/60 bg-base-100/60 p-1.5"
                        title={agent.name}
                      >
                        <Image
                          src={agent.icon}
                          alt={agent.name}
                          width={18}
                          height={18}
                          className={`h-[18px] w-[18px] object-contain ${agent.mono ? 'dark:invert' : ''}`}
                        />
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-base-300/80 bg-base-200/40 p-4 shadow-sm">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-base-content/50">
                {t('heroFlowTitle')}
              </p>
              <div className="space-y-2">
                {(
                  [
                    ['01', 'heroFlowIntent'],
                    ['02', 'heroFlowExecution'],
                    ['03', 'heroFlowEvidence'],
                    ['04', 'heroFlowReuse'],
                  ] as const
                ).map(([step, key], index) => (
                  <div key={key}>
                    <div className="flex items-center gap-3 rounded-xl border border-base-300/70 bg-base-100 px-3 py-2.5">
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-[10px] font-bold text-primary">
                        {step}
                      </span>
                      <span className="text-sm font-medium text-base-content/80">{t(key)}</span>
                      <span className="ml-auto text-xs text-base-content/30">✓</span>
                    </div>
                    {index < 3 ? <div className="ml-6 h-2 border-l border-dashed border-primary/30" /> : null}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* 2. Problem */}
        <Reveal>
          <section className="mt-16">
            <div className="mb-8 text-center">
              <h2 className="text-2xl font-bold md:text-3xl">{t('problemTitle')}</h2>
              <p className="mx-auto mt-2 max-w-xl text-sm text-base-content/60">{t('problemSubtitle')}</p>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-2xl border border-error/10 bg-error/5 p-6">
                <h3 className="mb-4 text-sm font-bold uppercase tracking-wider text-error">
                  {t('problemWithoutTitle')}
                </h3>
                <ul className="space-y-3">
                  {(['problemWithout1', 'problemWithout2', 'problemWithout3', 'problemWithout4'] as const).map(
                    (key) => (
                      <li key={key} className="flex items-start gap-2.5 text-sm text-base-content/70">
                        <span className="mt-0.5 shrink-0 text-base text-error">✕</span>
                        {t(key)}
                      </li>
                    ),
                  )}
                </ul>
              </div>
              <div className="rounded-2xl border border-success/10 bg-success/5 p-6">
                <h3 className="mb-4 text-sm font-bold uppercase tracking-wider text-success">
                  {t('problemWithTitle')}
                </h3>
                <ul className="space-y-3">
                  {(['problemWith1', 'problemWith2', 'problemWith3', 'problemWith4'] as const).map((key) => (
                    <li key={key} className="flex items-start gap-2.5 text-sm text-base-content/70">
                      <span className="mt-0.5 shrink-0 text-base text-success">✓</span>
                      {t(key)}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>
        </Reveal>

        {/* 3. What You Get — product evidence: plan, review, report */}
        <WhatYouGetSection />

        {/* 4. Development Cycle — Swimlane */}
        <SwimlaneDiagram />

        {/* 5. Context Layer */}
        <ContextLayerSection />

        {/* 6. Unified Search */}
        <Reveal>
          <section className="mt-16">
            <div className="mb-8 text-center">
              <h2 className="text-2xl font-bold md:text-3xl">{t('searchTitle')}</h2>
              <p className="mx-auto mt-2 max-w-xl text-sm text-base-content/60">{t('searchSubtitle')}</p>
            </div>
            <div className="grid gap-6 md:grid-cols-[1fr_280px]">
              {/* Search modal replica */}
              <div className="overflow-hidden rounded-2xl border border-base-300 bg-base-100 shadow-[0_18px_50px_-24px_rgba(15,23,42,0.35)]">
                {/* Search input */}
                <div className="flex items-center gap-3 px-4 py-3">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 shrink-0 text-base-content/30"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                  <span className="flex-1 text-sm text-base-content/70">auth flow</span>
                  <span className="hidden shrink-0 items-center rounded-md border border-base-content/10 bg-base-200/70 px-2.5 py-1 text-xs font-medium leading-none text-base-content/55 sm:inline-flex">
                    esc
                  </span>
                </div>

                <div className="border-t border-base-content/10" />

                {/* Type filter chips */}
                <div className="flex gap-2 overflow-x-auto px-4 py-3" style={{ scrollbarWidth: 'none' }}>
                  {[
                    { label: 'All', active: true },
                    { label: 'Document', active: false },
                    { label: 'Plan', active: false },
                    { label: 'Code Review', active: false },
                    { label: 'Report', active: false },
                    { label: '+4 more', active: false },
                  ].map((chip) => (
                    <span
                      key={chip.label}
                      className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ${chip.active ? 'bg-primary/15 text-primary' : 'bg-base-content/5 text-base-content/50'}`}
                    >
                      {chip.label}
                    </span>
                  ))}
                </div>

                <div className="border-t border-base-content/10" />

                {/* Results */}
                <div className="px-4 pb-1 pt-2 text-[11px] font-medium uppercase tracking-wider text-base-content/40">
                  6 results
                </div>
                <ul className="px-2 pb-2">
                  {(
                    [
                      {
                        icon: 'M12 6.253v13m0-13C10.832 5.483 9.246 5 7.5 5 4.462 5 2 6.567 2 8.5v10C2 16.567 4.462 15 7.5 15c1.746 0 3.332.483 4.5 1.253m0-10C13.168 5.483 14.754 5 16.5 5c3.038 0 5.5 1.567 5.5 3.5v10c0-1.933-2.462-3.5-5.5-3.5-1.746 0-3.332.483-4.5 1.253',
                        iconColor: 'text-info bg-info/10',
                        titleKey: 'searchResult1Title',
                        descKey: 'searchResult1Desc',
                        badgeKey: 'searchResult1Type',
                        status: null,
                        time: '3d ago',
                        active: true,
                      },
                      {
                        icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
                        iconColor: 'text-primary bg-primary/10',
                        titleKey: 'searchResult2Title',
                        descKey: 'searchResult2Desc',
                        badgeKey: 'searchResult2Type',
                        status: 'IN_PROGRESS',
                        statusColor: 'bg-info/15 text-info',
                        time: '2m ago',
                        active: false,
                      },
                      {
                        icon: 'M9 12h6m-6 4h3m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
                        iconColor: 'text-success bg-success/10',
                        titleKey: 'searchResult5Title',
                        descKey: 'searchResult5Desc',
                        badgeKey: 'searchResult5Type',
                        status: null,
                        time: '4h ago',
                        active: false,
                      },
                      {
                        icon: 'M2.886 4.18A11.982 11.982 0 0 1 11.99 0C18.624 0 24 5.376 24 12.009c0 3.64-1.62 6.903-4.18 9.105L2.887 4.18ZM1.817 5.626l16.556 16.556c-.524.33-1.075.62-1.65.866L.951 7.277c.247-.575.537-1.126.866-1.65ZM.322 9.163l14.515 14.515c-.71.172-1.443.282-2.195.322L0 11.358a12 12 0 0 1 .322-2.195Zm-.17 4.862 9.823 9.824a12.02 12.02 0 0 1-9.824-9.824Z',
                        iconColor: 'text-warning bg-warning/10',
                        titleKey: 'searchResult4Title',
                        descKey: 'searchResult4Desc',
                        badgeKey: 'searchResult4Type',
                        status: 'OPEN',
                        statusColor: 'bg-warning/15 text-warning',
                        time: '1h ago',
                        active: false,
                        filled: true,
                      },
                      {
                        icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
                        iconColor: 'text-accent bg-accent/10',
                        titleKey: 'searchResult3Title',
                        descKey: 'searchResult3Desc',
                        badgeKey: 'searchResult3Type',
                        status: 'COMPLETED',
                        statusColor: 'bg-success/15 text-success',
                        time: '15m ago',
                        active: false,
                      },
                      {
                        icon: 'M7 7h10M7 12h4m-4 5h10M5 3v18m14-4l2 2-2 2M19 15l-2 2 2 2',
                        iconColor: 'text-error bg-error/10',
                        titleKey: 'searchResult6Title',
                        descKey: 'searchResult6Desc',
                        badgeKey: 'searchResult6Type',
                        status: null,
                        time: '1d ago',
                        active: false,
                      },
                    ] as const
                  ).map((item) => (
                    <li
                      key={item.titleKey}
                      className={`flex items-start gap-3 rounded-lg px-3 py-2.5 ${item.active ? 'bg-primary/10' : ''}`}
                    >
                      <div
                        className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md ${item.iconColor}`}
                      >
                        {'filled' in item && item.filled ? (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-3.5 w-3.5"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                          >
                            <path d={item.icon} />
                          </svg>
                        ) : (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-3.5 w-3.5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={item.icon} />
                          </svg>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="truncate text-sm font-medium text-base-content">{t(item.titleKey)}</span>
                          {item.badgeKey ? (
                            <span className="shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-medium leading-none bg-base-content/8 text-base-content/50">
                              {t(item.badgeKey)}
                            </span>
                          ) : null}
                          {item.status ? (
                            <span
                              className={`shrink-0 rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase leading-none ${item.statusColor}`}
                            >
                              {item.status}
                            </span>
                          ) : null}
                        </div>
                        <span className="mt-0.5 line-clamp-1 text-xs text-base-content/40">{t(item.descKey)}</span>
                      </div>
                      <span className="mt-0.5 shrink-0 text-[11px] text-base-content/50">{item.time}</span>
                    </li>
                  ))}
                </ul>

                {/* Keyboard hints */}
                <div className="hidden items-center gap-4 border-t border-base-content/10 px-4 py-2 text-[11px] text-base-content/35 sm:flex">
                  <span className="flex items-center gap-1">
                    <span className="inline-flex items-center rounded-md border border-base-content/10 bg-base-200/70 px-1.5 py-0.5 text-[10px] font-medium leading-none text-base-content/55">
                      &uarr;
                    </span>
                    <span className="inline-flex items-center rounded-md border border-base-content/10 bg-base-200/70 px-1.5 py-0.5 text-[10px] font-medium leading-none text-base-content/55">
                      &darr;
                    </span>
                    navigate
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="inline-flex items-center rounded-md border border-base-content/10 bg-base-200/70 px-1.5 py-0.5 text-[10px] font-medium leading-none text-base-content/55">
                      &crarr;
                    </span>
                    select
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="inline-flex items-center rounded-md border border-base-content/10 bg-base-200/70 px-1.5 py-0.5 text-[10px] font-medium leading-none text-base-content/55">
                      esc
                    </span>
                    close
                  </span>
                </div>
              </div>

              {/* Benefits */}
              <div className="grid auto-rows-fr gap-3">
                {(
                  [
                    {
                      titleKey: 'searchBenefit1Title',
                      descKey: 'searchBenefit1Desc',
                      icon: 'M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z',
                    },
                    {
                      titleKey: 'searchBenefit2Title',
                      descKey: 'searchBenefit2Desc',
                      icon: 'M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1',
                    },
                    {
                      titleKey: 'searchBenefit3Title',
                      descKey: 'searchBenefit3Desc',
                      icon: 'M13 10V3L4 14h7v7l9-11h-7z',
                    },
                    {
                      titleKey: 'searchBenefit4Title',
                      descKey: 'searchBenefit4Desc',
                      icon: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z',
                    },
                  ] as const
                ).map((b) => (
                  <div key={b.titleKey} className="rounded-2xl border border-base-300 bg-base-100 p-3.5 shadow-xs">
                    <div className="flex items-center gap-2">
                      <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-3.5 w-3.5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={1.8}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d={b.icon} />
                        </svg>
                      </div>
                      <h3 className="text-sm font-semibold">{t(b.titleKey)}</h3>
                    </div>
                    <p className="mt-1.5 text-xs leading-relaxed text-base-content/60">{t(b.descKey)}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </Reveal>

        {/* 7. Differentiators — many engines, human judgment, team records */}
        <Reveal>
          <section className="mt-16">
            <div className="mb-8 text-center">
              <h2 className="text-2xl font-bold md:text-3xl">{t('valueTitle')}</h2>
              <p className="mx-auto mt-3 max-w-2xl text-sm text-base-content/60">{t('valueDescription')}</p>
            </div>
            <div className="grid gap-4 md:grid-cols-3">
              {(
                [
                  {
                    key: 'engines',
                    icon: 'M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z',
                  },
                  {
                    key: 'judgment',
                    icon: 'M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z',
                  },
                  {
                    key: 'records',
                    icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
                  },
                ] as const
              ).map((item, i) => (
                <Reveal key={item.key} delay={i * 80} className="h-full">
                  <div className="h-full rounded-2xl border border-base-300 bg-base-100 p-5 shadow-xs">
                    <div className="mb-3 flex items-center gap-2">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={1.8}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d={item.icon} />
                        </svg>
                      </div>
                      <h3 className="text-sm font-semibold">{t(`differentiators.${item.key}.title`)}</h3>
                    </div>
                    <p className="text-sm leading-relaxed text-base-content/60">
                      {t(`differentiators.${item.key}.description`)}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
            <div className="mt-8">
              <p className="mb-4 text-center text-sm font-medium text-base-content/70">{t('valueListLabel')}</p>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {(['valueItem1', 'valueItem2', 'valueItem3', 'valueItem4'] as const).map((key) => (
                  <div
                    key={key}
                    className="flex items-start gap-3 rounded-xl border border-base-300 bg-base-100 px-4 py-3 shadow-xs"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="mt-0.5 h-4 w-4 shrink-0 text-primary"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm text-base-content/80">{t(key)}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </Reveal>

        {/* 8. CLI vs Runner */}
        <Reveal>
          <section className="mt-16">
            <div className="mb-8 text-center">
              <h2 className="text-2xl font-bold md:text-3xl">{t('positioningTitle')}</h2>
              <p className="mx-auto mt-2 max-w-xl text-sm text-base-content/60">{t('positioningSubtitle')}</p>
            </div>
            <div className="grid gap-6 md:grid-cols-2">
              {/* CLI Card */}
              <div className="flex flex-col rounded-2xl border border-base-300 bg-base-100 p-6 shadow-xs">
                <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-secondary/10 text-secondary">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <h3 className="mb-2 text-xl font-semibold">{t('positioningCliTitle')}</h3>
                <p className="mb-4 text-sm text-base-content/70">{t('positioningCliDesc')}</p>
                {/* Badges */}
                <div className="mb-4 flex flex-wrap gap-2">
                  <a href="https://github.com/AgentTeamsRun/cli" target="_blank" rel="noopener noreferrer">
                    {/* eslint-disable-next-line @next/next/no-img-element -- shields.io badge is an external static image. */}
                    <img
                      src="https://img.shields.io/badge/GitHub-AgentTeamsRun%2Fcli-181717?logo=github"
                      alt="GitHub"
                      className="h-5"
                    />
                  </a>
                  <a href="https://www.npmjs.com/package/@agentteams/cli" target="_blank" rel="noopener noreferrer">
                    {/* eslint-disable-next-line @next/next/no-img-element -- shields.io badge is an external static image. */}
                    <img
                      src="https://img.shields.io/npm/v/@agentteams/cli?color=cb3837&logo=npm"
                      alt="npm version"
                      className="h-5"
                    />
                  </a>
                </div>
                <ul className="space-y-3">
                  {(['positioningCliFeature1', 'positioningCliFeature2', 'positioningCliFeature3'] as const).map(
                    (key) => (
                      <li key={key} className="flex items-start gap-3 text-sm text-base-content/80">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="mt-0.5 h-4 w-4 shrink-0 text-secondary"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        {t(key)}
                      </li>
                    ),
                  )}
                </ul>

                {/* CLI Terminal Mockup */}
                <div className="my-auto pt-5">
                  <AnimatedTerminalMockup />
                </div>
              </div>

              {/* Runner Card */}
              <div className="flex flex-col rounded-2xl border border-base-300 bg-base-100 p-6 shadow-xs">
                <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M14.615 1.595a.75.75 0 01.359.852L12.982 9.75h7.268a.75.75 0 01.548 1.262l-10.5 11.25a.75.75 0 01-1.272-.71l1.992-7.302H3.818a.75.75 0 01-.548-1.262l10.5-11.25a.75.75 0 01.845-.143z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <h3 className="mb-2 text-xl font-semibold">{t('positioningRunnerTitle')}</h3>
                <p className="mb-4 text-sm text-base-content/70">{t('positioningRunnerDesc')}</p>
                {/* Badges */}
                <div className="mb-4 flex flex-wrap gap-2">
                  <a href="https://github.com/AgentTeamsRun/runner" target="_blank" rel="noopener noreferrer">
                    {/* eslint-disable-next-line @next/next/no-img-element -- shields.io badge is an external static image. */}
                    <img
                      src="https://img.shields.io/badge/GitHub-AgentTeamsRun%2Frunner-181717?logo=github"
                      alt="GitHub"
                      className="h-5"
                    />
                  </a>
                  <a href="https://www.npmjs.com/package/@agentteams/runner" target="_blank" rel="noopener noreferrer">
                    {/* eslint-disable-next-line @next/next/no-img-element -- shields.io badge is an external static image. */}
                    <img
                      src="https://img.shields.io/npm/v/@agentteams/runner?color=cb3837&logo=npm"
                      alt="npm version"
                      className="h-5"
                    />
                  </a>
                </div>
                <ul className="space-y-3">
                  {(
                    ['positioningRunnerFeature1', 'positioningRunnerFeature2', 'positioningRunnerFeature3'] as const
                  ).map((key) => (
                    <li key={key} className="flex items-start gap-3 text-sm text-base-content/80">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="mt-0.5 h-4 w-4 shrink-0 text-primary"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {t(key)}
                    </li>
                  ))}
                </ul>

                {/* Runner Mobile Mockup */}
                <div className="my-auto flex justify-center pt-5">
                  <AnimatedPhoneMockup />
                </div>
              </div>
            </div>
          </section>
        </Reveal>

        {/* 8b. Workspace host (Orca / herdr) integration */}
        <WorkspaceHostSection />

        {/* 8c. Desktop App Download */}
        <DesktopDownloadSection />

        {/* 9. Bottom CTA */}
        <Reveal>
          <section className="relative mt-16 overflow-hidden rounded-2xl border border-primary/20 bg-primary/5 p-8 text-center md:p-12">
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,rgba(59,130,246,0.08),transparent_40%),radial-gradient(circle_at_top_right,rgba(16,185,129,0.06),transparent_35%)]"
            />
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 opacity-[0.02]"
              style={{
                backgroundImage: 'radial-gradient(circle, currentColor 1px, transparent 1px)',
                backgroundSize: '20px 20px',
              }}
            />
            <div className="relative">
              <h2 className="text-2xl font-bold md:text-3xl">{t('bottomCtaTitle')}</h2>
              <p className="mx-auto mt-3 max-w-md text-sm text-base-content/60">{t('bottomCtaDescription')}</p>
              <div className="mt-6 flex flex-wrap justify-center gap-2">
                <Link href="/auth/register" className="btn btn-primary">
                  {t('primaryCta')}
                </Link>
                <Link href="/auth/login" className="btn btn-outline">
                  {t('secondaryCta')}
                </Link>
              </div>
            </div>
          </section>
        </Reveal>

        {/* Footer */}
        <footer className="mt-auto pt-10">
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-base-300/70 py-4 text-xs text-base-content/60">
            <p>&copy; {new Date().getFullYear()} AgentTeams</p>
            <div className="flex items-center gap-3">
              <Link href="/terms" className="link link-hover">
                {t('terms')}
              </Link>
              <Link href="/privacy" className="link link-hover">
                {t('privacy')}
              </Link>
            </div>
          </div>
        </footer>
      </div>
    </main>
  );
}
